import React, { useState, useMemo } from "react";
import { Icon } from "./Icons";
import TherapistPortrait from "./TherapistPortrait";
import { THERAPISTS } from "./therapists";
import "./BookingCalendar.css";

/**
 * BookingCalendar — the multi-step booking flow.
 *
 * Two modes:
 *
 * 1. WITH preselected therapist (entry via /book/:slug):
 *    Steps: service → date → time → details
 *
 * 2. WITHOUT therapist (entry via "Book" button → /booking):
 *    Steps: service → therapist → date → time → details
 *
 *    The therapist step shows only clinicians who offer the chosen
 *    service. Availability uses the same availabilitySeed-driven
 *    logic as the therapist's profile, so what you see here matches
 *    what you'd see arriving via /book/:slug.
 *
 *    Free consultation flow (service.id === "consultation") is
 *    handled at the practice level — any therapist can take a
 *    consultation, and Harbor's intake coordinator routes it.
 *    For UX, we still let the user pick a clinician here so they
 *    know who they'll be talking to, OR they can pick "any
 *    available" for the soonest slot.
 */

// Generic services for the no-therapist-yet flow
const PRACTICE_SERVICES = [
    {
        id: "consultation",
        name: "Free Consultation",
        duration: "20 minutes",
        price: "Free",
        priceCents: 0,
        priceDetail: "No charge · phone call",
        description:
            "A no-pressure call to talk about what you're looking for and whether we'd be a good fit.",
        recommended: true,
    },
    {
        id: "individual",
        name: "Individual Session",
        duration: "50 minutes",
        price: "From $140",
        priceCents: null, // varies by therapist
        priceDetail: "Fees vary by therapist ($140–$200)",
        description:
            "Ongoing therapy for adults. Anxiety, burnout, relationships, life transitions, and more.",
    },
    {
        id: "couples",
        name: "Couples Session",
        duration: "60 minutes",
        price: "From $185",
        priceCents: null,
        priceDetail: "Fees vary by therapist ($185–$225)",
        description:
            "Communication, conflict, intimacy, and the question of what you both want from this.",
    },
];

// Build services for a specific therapist using their fees
const servicesForTherapist = (therapist) => {
    const services = [
        {
            id: "consultation",
            name: "Free Consultation",
            duration: "20 minutes",
            price: "Free",
            priceCents: 0,
            priceDetail: "No charge · phone call",
            description: `A no-pressure call with ${therapist.name.split(" ").slice(-1)[0]} to see if you're a good fit.`,
            recommended: true,
        },
        {
            id: "individual",
            name: "Individual Session",
            duration: "50 minutes",
            price: `$${therapist.fee}`,
            priceCents: therapist.fee * 100,
            priceDetail: `$${therapist.fee} per session`,
            description: therapist.bioShort,
        },
    ];
    if (therapist.offersCouples) {
        services.push({
            id: "couples",
            name: "Couples Session",
            duration: "60 minutes",
            price: `$${therapist.coupleFee}`,
            priceCents: therapist.coupleFee * 100,
            priceDetail: `$${therapist.coupleFee} per session`,
            description: "Communication, conflict, intimacy, and what you both want from this.",
        });
    }
    return services;
};

/**
 * Find therapists who offer a given service.
 * Couples → only therapists with offersCouples=true.
 * Consultation/individual → all therapists.
 */
const therapistsForService = (serviceId) => {
    if (serviceId === "couples") {
        return THERAPISTS.filter((t) => t.offersCouples);
    }
    return THERAPISTS;
};

/**
 * Hydrate a service with the chosen therapist's actual fee.
 * Used when the user picked a generic service first, then a therapist.
 */
const finalizeService = (service, therapist) => {
    if (service.id === "consultation") return service;
    if (service.id === "individual") {
        return {
            ...service,
            price: `$${therapist.fee}`,
            priceCents: therapist.fee * 100,
            priceDetail: `$${therapist.fee} per session`,
        };
    }
    if (service.id === "couples") {
        return {
            ...service,
            price: `$${therapist.coupleFee}`,
            priceCents: therapist.coupleFee * 100,
            priceDetail: `$${therapist.coupleFee} per session`,
        };
    }
    return service;
};

const generateDates = () => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 21; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + i);
        dates.push(date);
    }
    return dates;
};

const WEEKDAY_SLOTS = [
    "9:00 AM", "10:00 AM", "11:00 AM",
    "12:00 PM", "1:00 PM", "2:00 PM",
    "3:00 PM", "4:00 PM", "5:00 PM",
];
const SATURDAY_SLOTS = ["10:00 AM", "11:00 AM", "12:00 PM", "1:00 PM"];

/**
 * Therapist-aware availability simulator.
 * Each therapist's `availabilitySeed` produces a distinct booking
 * pattern — high-seed therapists are mostly booked, low-seed
 * therapists have wider availability.
 *
 * IMPORTANT: This function MUST produce the same result regardless of
 * how the booking flow was entered. That's how /book/:slug and
 * /booking → choose-therapist show identical availability.
 */
const getSlotsForDate = (date, seed = 13) => {
    const day = date.getDay();
    if (day === 0) return [];
    const baseSlots = day === 6 ? SATURDAY_SLOTS : WEEKDAY_SLOTS;

    const dateSeed = date.getDate() + date.getMonth() * 31;
    const bookingDensity = Math.min(0.75, Math.max(0.2, seed / 55));

    return baseSlots.map((time, i) => {
        const hash = (dateSeed * 7 + i * 13 + seed) % 100;
        const booked = hash < bookingDensity * 100;
        return { time, booked };
    });
};

const filterPastSlots = (slots, date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dateStart = new Date(date);
    dateStart.setHours(0, 0, 0, 0);
    if (dateStart.getTime() !== today.getTime()) return slots;

    const now = new Date();
    return slots.filter(({ time }) => {
        const match = time.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!match) return true;
        let h = parseInt(match[1]);
        const m = parseInt(match[2]);
        const isPM = match[3].toUpperCase() === "PM";
        if (isPM && h !== 12) h += 12;
        if (!isPM && h === 12) h = 0;
        const slotDate = new Date();
        slotDate.setHours(h, m, 0, 0);
        return slotDate > now;
    });
};

/**
 * Count availability across all therapists who offer the given service
 * over the next 7 days. Used in the therapist-pick step to show "5
 * slots this week" style hints.
 */
const countWeekAvailability = (therapist) => {
    let total = 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
        const d = new Date(today);
        d.setDate(today.getDate() + i);
        const slots = filterPastSlots(getSlotsForDate(d, therapist.availabilitySeed), d);
        total += slots.filter((s) => !s.booked).length;
    }
    return total;
};

function BookingCalendar({ therapist: preselected, onBookingComplete }) {
    // step values:
    // 1: service, 2: therapist (skipped if preselected), 3: date, 4: time, 5: details
    const [step, setStep] = useState(1);
    const [selectedService, setSelectedService] = useState(null);
    const [selectedTherapist, setSelectedTherapist] = useState(preselected || null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedTime, setSelectedTime] = useState(null);
    const [clientInfo, setClientInfo] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
    });

    const hasPreselected = !!preselected;

    // Services list depends on whether we have a therapist already
    const services = useMemo(
        () => (preselected ? servicesForTherapist(preselected) : PRACTICE_SERVICES),
        [preselected]
    );

    // Active therapist for availability lookups (preselected OR chosen in step 2)
    const activeTherapist = preselected || selectedTherapist;
    const seed = activeTherapist?.availabilitySeed ?? 13;
    const dates = useMemo(generateDates, []);

    const timezone = useMemo(() => {
        try {
            return Intl.DateTimeFormat().resolvedOptions().timeZone;
        } catch {
            return "Local time";
        }
    }, []);

    const slotsForSelectedDate = useMemo(() => {
        if (!selectedDate || !activeTherapist) return [];
        return filterPastSlots(getSlotsForDate(selectedDate, seed), selectedDate);
    }, [selectedDate, seed, activeTherapist]);

    const hasNoSlots = (date) => {
        if (!activeTherapist) return false;
        return filterPastSlots(getSlotsForDate(date, seed), date).length === 0;
    };
    const isFullyBooked = (date) => {
        if (!activeTherapist) return false;
        const slots = filterPastSlots(getSlotsForDate(date, seed), date);
        return slots.length > 0 && slots.every((s) => s.booked);
    };

    // Therapists who can fulfill the chosen service
    const eligibleTherapists = useMemo(() => {
        if (!selectedService) return [];
        return therapistsForService(selectedService.id);
    }, [selectedService]);

    // Step navigation helpers
    const handleServiceSelect = (service) => {
        setSelectedService(service);
        // If we have a preselected therapist, skip to date pick (step 3)
        // Otherwise, go to therapist pick (step 2)
        setStep(hasPreselected ? 3 : 2);
    };

    const handleTherapistSelect = (t) => {
        setSelectedTherapist(t);
        // Hydrate the service with this therapist's actual fee
        setSelectedService((prev) => finalizeService(prev, t));
        setStep(3);
    };

    const handleDateSelect = (date) => {
        if (hasNoSlots(date) || isFullyBooked(date)) return;
        setSelectedDate(date);
        setStep(4);
    };

    const handleTimeSelect = (time) => {
        setSelectedTime(time);
        setStep(5);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onBookingComplete({
            service: selectedService,
            date: selectedDate,
            time: selectedTime,
            client: clientInfo,
            therapist: activeTherapist,
        });
    };

    // Step labels — different sets depending on flow
    const stepLabels = hasPreselected
        ? ["Service", "Date", "Time", "Details"]
        : ["Service", "Therapist", "Date", "Time", "Details"];

    // Map internal step numbers to progress indicator step numbers
    // When preselected: step 1=Service, 3=Date, 4=Time, 5=Details (no step 2)
    // We translate this for the progress display
    const displayStep = hasPreselected
        ? step === 1 ? 1 : step - 1
        : step;

    const formatDate = (date) =>
        date.toLocaleDateString("en-US", {
            weekday: "short",
            month: "short",
            day: "numeric",
        });

    const formatDateFull = (date) =>
        date.toLocaleDateString("en-US", {
            weekday: "long",
            month: "long",
            day: "numeric",
            year: "numeric",
        });

    return (
        <div className="booking-widget">
            <div className="booking-progress">
                {stepLabels.map((label, i) => {
                    const stepNum = i + 1;
                    return (
                        <React.Fragment key={label}>
                            <div
                                className={`progress-step ${displayStep >= stepNum ? "active" : ""} ${displayStep > stepNum ? "complete" : ""}`}
                            >
                                <div className="progress-circle">
                                    {displayStep > stepNum ? <Icon name="check" size={14} /> : stepNum}
                                </div>
                                <span>{label}</span>
                            </div>
                            {i < stepLabels.length - 1 && <div className="progress-line"></div>}
                        </React.Fragment>
                    );
                })}
            </div>

            {/* STEP 1: SERVICE */}
            {step === 1 && (
                <div className="booking-step animate-in">
                    <h3>Select a service</h3>
                    <p className="step-subtitle">
                        {preselected
                            ? `Available with ${preselected.name.split(" ").slice(-1)[0]}.`
                            : "Not sure which? Start with the free consultation."}
                    </p>
                    <div className="service-grid">
                        {services.map((service) => (
                            <button
                                key={service.id}
                                className={`service-card ${service.recommended ? "service-recommended" : ""}`}
                                onClick={() => handleServiceSelect(service)}
                            >
                                {service.recommended && (
                                    <span className="service-badge">Recommended for new clients</span>
                                )}
                                <div className="service-header">
                                    <h4>{service.name}</h4>
                                    <span className="service-price">{service.price}</span>
                                </div>
                                <p className="service-description">{service.description}</p>
                                <p className="service-duration">
                                    <Icon name="clock" size={14} />
                                    <span>{service.duration}</span>
                                </p>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* STEP 2: THERAPIST (only when no preselection) */}
            {step === 2 && !hasPreselected && (
                <div className="booking-step animate-in">
                    <button className="back-button" onClick={() => setStep(1)}>
                        ← Back to services
                    </button>
                    <h3>Choose your therapist</h3>
                    <p className="step-subtitle">
                        {selectedService.id === "couples"
                            ? `${eligibleTherapists.length} therapists offer couples sessions.`
                            : `Any of our ${eligibleTherapists.length} clinicians can see you for this.`}
                        {" "}Availability is shown for the next 7 days.
                    </p>

                    <div className="therapist-pick-grid">
                        {eligibleTherapists.map((t) => {
                            const weekSlots = countWeekAvailability(t);
                            const availClass =
                                weekSlots === 0 ? "avail-none" :
                                weekSlots <= 3 ? "avail-limited" :
                                "avail-open";
                            return (
                                <button
                                    key={t.slug}
                                    className="therapist-pick-card"
                                    onClick={() => handleTherapistSelect(t)}
                                >
                                    <div className="therapist-pick-portrait">
                                        <TherapistPortrait therapist={t} size="card" />
                                    </div>
                                    <div className="therapist-pick-body">
                                        <div className="therapist-pick-header">
                                            <div>
                                                <h4>{t.name}</h4>
                                                <span className="therapist-pick-cred">
                                                    {t.credential} · {t.pronouns}
                                                </span>
                                            </div>
                                            <span className={`therapist-pick-fee`}>
                                                {selectedService.id === "consultation"
                                                    ? "Free"
                                                    : selectedService.id === "couples"
                                                    ? `$${t.coupleFee}`
                                                    : `$${t.fee}`}
                                            </span>
                                        </div>
                                        <p className="therapist-pick-bio">{t.bioShort}</p>
                                        <div className="therapist-pick-tags">
                                            {t.specializations.slice(0, 3).map((s) => (
                                                <span key={s} className="therapist-tag">{s}</span>
                                            ))}
                                        </div>
                                        <div className="therapist-pick-footer">
                                            <span className={`avail-badge ${availClass}`}>
                                                <span className="avail-dot" />
                                                {weekSlots === 0
                                                    ? "Booked next 7 days"
                                                    : `${weekSlots} slot${weekSlots === 1 ? "" : "s"} this week`}
                                            </span>
                                            <span className="therapist-pick-link">Select →</span>
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* STEP 3: DATE */}
            {step === 3 && (
                <div className="booking-step animate-in">
                    <button className="back-button" onClick={() => setStep(hasPreselected ? 1 : 2)}>
                        ← Back to {hasPreselected ? "services" : "therapists"}
                    </button>
                    <h3>Choose a date</h3>
                    <p className="step-subtitle">
                        {selectedService.name} with {activeTherapist.name} · {selectedService.duration}
                    </p>

                    <div className="calendar-legend">
                        <span className="legend-item">
                            <span className="legend-swatch legend-available"></span>
                            Available
                        </span>
                        <span className="legend-item">
                            <span className="legend-swatch legend-limited"></span>
                            Limited availability
                        </span>
                        <span className="legend-item">
                            <span className="legend-swatch legend-unavailable"></span>
                            Unavailable
                        </span>
                    </div>

                    <div className="date-grid">
                        {dates.map((date, index) => {
                            const slots = filterPastSlots(getSlotsForDate(date, seed), date);
                            const totalSlots = slots.length;
                            const availableSlots = slots.filter((s) => !s.booked).length;
                            const isUnavailable = totalSlots === 0 || availableSlots === 0;
                            const isLimited = !isUnavailable && availableSlots <= 2;

                            return (
                                <button
                                    key={index}
                                    className={`date-card ${isUnavailable ? "date-unavailable" : ""} ${isLimited ? "date-limited" : ""}`}
                                    onClick={() => handleDateSelect(date)}
                                    disabled={isUnavailable}
                                >
                                    <span className="date-day">
                                        {date.toLocaleDateString("en-US", { weekday: "short" })}
                                    </span>
                                    <span className="date-number">{date.getDate()}</span>
                                    <span className="date-month">
                                        {date.toLocaleDateString("en-US", { month: "short" })}
                                    </span>
                                    <span className="date-status">
                                        {isUnavailable
                                            ? "—"
                                            : isLimited
                                            ? `${availableSlots} left`
                                            : `${availableSlots} open`}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* STEP 4: TIME */}
            {step === 4 && (
                <div className="booking-step animate-in">
                    <button className="back-button" onClick={() => setStep(3)}>
                        ← Back to dates
                    </button>
                    <h3>Select a time</h3>
                    <p className="step-subtitle">
                        {selectedService.name} with {activeTherapist.name} · {formatDateFull(selectedDate)}
                    </p>
                    <p className="timezone-note">
                        <Icon name="pin" size={14} />
                        <span>All times shown in your timezone ({timezone})</span>
                    </p>

                    <div className="time-grid">
                        {slotsForSelectedDate.map(({ time, booked }, index) => (
                            <button
                                key={index}
                                className={`time-slot ${booked ? "time-slot-booked" : ""}`}
                                onClick={() => !booked && handleTimeSelect(time)}
                                disabled={booked}
                            >
                                <span>{time}</span>
                                {booked && <span className="time-slot-label">Booked</span>}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* STEP 5: DETAILS */}
            {step === 5 && (
                <div className="booking-step animate-in">
                    <button className="back-button" onClick={() => setStep(4)}>
                        ← Back to times
                    </button>
                    <h3>Your information</h3>
                    <p className="step-subtitle">
                        Just for booking. Clinical intake comes through the secure
                        portal after we connect.
                    </p>

                    <div className="booking-summary">
                        <div className="summary-item">
                            <span className="summary-label">Therapist</span>
                            <span className="summary-value">
                                {activeTherapist.name}, {activeTherapist.credential}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Service</span>
                            <span className="summary-value">{selectedService.name}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Date &amp; time</span>
                            <span className="summary-value">
                                {formatDate(selectedDate)} · {selectedTime}
                            </span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Duration</span>
                            <span className="summary-value">{selectedService.duration}</span>
                        </div>
                        <div className="summary-item">
                            <span className="summary-label">Fee</span>
                            <span className="summary-value">{selectedService.priceDetail}</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="client-form">
                        <div className="form-row">
                            <div className="form-group">
                                <label>First name</label>
                                <input
                                    type="text"
                                    required
                                    value={clientInfo.firstName}
                                    onChange={(e) =>
                                        setClientInfo({ ...clientInfo, firstName: e.target.value })
                                    }
                                />
                            </div>
                            <div className="form-group">
                                <label>Last name</label>
                                <input
                                    type="text"
                                    required
                                    value={clientInfo.lastName}
                                    onChange={(e) =>
                                        setClientInfo({ ...clientInfo, lastName: e.target.value })
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Email</label>
                            <input
                                type="email"
                                required
                                value={clientInfo.email}
                                onChange={(e) =>
                                    setClientInfo({ ...clientInfo, email: e.target.value })
                                }
                            />
                        </div>

                        <div className="form-group">
                            <label>Phone</label>
                            <input
                                type="tel"
                                required
                                placeholder="(555) 123-4567"
                                value={clientInfo.phone}
                                onChange={(e) =>
                                    setClientInfo({ ...clientInfo, phone: e.target.value })
                                }
                            />
                        </div>

                        <div className="privacy-note">
                            <Icon name="lock" size={16} />
                            <p>
                                Your contact information is used only for scheduling and
                                confirmation. Clinical intake — including health history
                                and emergency contact — is collected separately through
                                the HIPAA-compliant client portal after booking is
                                confirmed.
                            </p>
                        </div>

                        <button type="submit" className="btn-primary btn-large">
                            Continue to questionnaire
                        </button>

                        <p className="form-note">
                            By booking, you agree to the 24-hour cancellation policy.
                        </p>
                    </form>
                </div>
            )}
        </div>
    );
}

export default BookingCalendar;